import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaInstagram, FaTwitter, FaFacebook, FaGlobe } from 'react-icons/fa';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { setLoading, setError, setCurrentArtist } from '../../features/marketplace/marketplaceSlice';
import MarketplaceLayout from './MarketplaceLayout';
import ProductCard from './ProductCard';
import { SEOHead } from '../SEO';

const Container = styled.div`
  color: white;
`;

const BackButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin-bottom: 2rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 3rem;
  text-align: center;
`;

const CoverImage = styled.div`
  width: 100%;
  height: 200px;
  background-image: ${props => props.src ? `url(${props.src})` : 'linear-gradient(90deg, #4a90e2, #357abd)'};
  background-size: cover;
  background-position: center;
  border-radius: 10px;
  margin-bottom: 2rem;
  position: relative;
`;

const AvatarContainer = styled.div`
  position: absolute;
  bottom: -50px;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  background: #0a1128;
`;

const Avatar = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
`;

const ArtistName = styled.h1`
  font-size: 2rem;
  margin: 3rem 0 0.5rem;
`;

const ArtistBio = styled.p`
  font-size: 1rem;
  color: #ccc;
  max-width: 600px;
  margin: 0 auto 1.5rem;
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SocialLink = styled(motion.a)`
  background: rgba(255, 255, 255, 0.1);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #4a90e2;
  }
`;

const Stats = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
`;

const Stat = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #888;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  margin: 2rem 0 1.5rem;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#4a90e2' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.active ? '#4a90e2' : 'rgba(255, 255, 255, 0.2)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
`;

const ArtistProfile = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error, currentArtist } = useSelector(state => state.marketplace);
  
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  useEffect(() => {
    const fetchArtistProfile = async () => {
      try {
        dispatch(setLoading(true));
        
        // Fetch artist
        const artistRef = doc(db, 'users', artistId);
        const artistSnap = await getDoc(artistRef);
        
        if (!artistSnap.exists()) {
          throw new Error('Artist not found');
        }
        
        const artistData = {
          ...artistSnap.data(),
          $id: artistSnap.id
        };
        
        dispatch(setCurrentArtist(artistData));
        
        // Fetch artist's products
        const productsRef = collection(db, 'products');
        const productsQuery = query(
          productsRef,
          where('sellerId', '==', artistId),
          where('status', '==', 'active')
        );
        
        const productsSnap = await getDocs(productsQuery);
        const productsData = productsSnap.docs.map(doc => ({
          ...doc.data(),
          $id: doc.id
        }));
        
        setProducts(productsData);
        
      } catch (err) {
        dispatch(setError(err.message));
      } finally {
        dispatch(setLoading(false));
      }
    };
    
    fetchArtistProfile();
  }, [dispatch, artistId]);
  
  const handleGoBack = () => {
    navigate(-1);
  };
  
  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(product => product.category === selectedCategory);
  
  // Get unique categories from artist's products
  const categories = ['all', ...new Set(products.map(product => product.category))];
  
  if (loading) {
    return (
      <MarketplaceLayout>
        <Container>
          <p>Loading artist profile...</p>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (error) {
    return (
      <MarketplaceLayout>
        <Container>
          <p>Error: {error}</p>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Go Back
          </BackButton>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  if (!currentArtist) {
    return (
      <MarketplaceLayout>
        <Container>
          <p>Artist not found</p>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Go Back
          </BackButton>
        </Container>
      </MarketplaceLayout>
    );
  }
  
  const { 
    displayName, 
    email, 
    photoURL, 
    coverImage, 
    bio, 
    socialLinks = {},
    productCount = products.length,
    followerCount = 0,
    salesCount = 0
  } = currentArtist;
  
  const name = displayName || email?.split('@')[0] || 'Artist';
  
  return (
    <MarketplaceLayout>
      <SEOHead
        title={`${name} - Artist Profile`}
        description={bio || `Check out ${name}'s products on Konzo Marketplace`}
        keywords={`${name}, artist, konzo vibez, marketplace, music producer`}
        canonicalUrl={`/marketplace/artist/${artistId}`}
        ogImage={photoURL}
        ogType="profile"
      />
      
      <Container>
        <BackButton 
          onClick={handleGoBack}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft /> Back to Marketplace
        </BackButton>
        
        <ProfileHeader>
          <CoverImage src={coverImage}>
            <AvatarContainer>
              <Avatar 
                src={photoURL || '/default-avatar.jpg'} 
                alt={name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-avatar.jpg';
                }}
              />
            </AvatarContainer>
          </CoverImage>
          
          <ArtistName>{name}</ArtistName>
          
          {bio && <ArtistBio>{bio}</ArtistBio>}
          
          <SocialLinks>
            {socialLinks.instagram && (
              <SocialLink 
                href={socialLinks.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaInstagram />
              </SocialLink>
            )}
            
            {socialLinks.twitter && (
              <SocialLink 
                href={socialLinks.twitter} 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTwitter />
              </SocialLink>
            )}
            
            {socialLinks.facebook && (
              <SocialLink 
                href={socialLinks.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaFacebook />
              </SocialLink>
            )}
            
            {socialLinks.website && (
              <SocialLink 
                href={socialLinks.website} 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaGlobe />
              </SocialLink>
            )}
          </SocialLinks>
          
          <Stats>
            <Stat>
              <StatValue>{productCount}</StatValue>
              <StatLabel>Products</StatLabel>
            </Stat>
            
            <Stat>
              <StatValue>{followerCount}</StatValue>
              <StatLabel>Followers</StatLabel>
            </Stat>
            
            <Stat>
              <StatValue>{salesCount}</StatValue>
              <StatLabel>Sales</StatLabel>
            </Stat>
          </Stats>
        </ProfileHeader>
        
        <SectionTitle>Products by {name}</SectionTitle>
        
        {categories.length > 1 && (
          <FilterButtons>
            {categories.map(category => (
              <FilterButton
                key={category}
                active={selectedCategory === category}
                onClick={() => setSelectedCategory(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </FilterButton>
            ))}
          </FilterButtons>
        )}
        
        {filteredProducts.length > 0 ? (
          <ProductsGrid>
            {filteredProducts.map(product => (
              <ProductCard 
                key={product.$id} 
                product={product} 
                artist={currentArtist}
              />
            ))}
          </ProductsGrid>
        ) : (
          <EmptyState>
            <p>No products found in this category</p>
          </EmptyState>
        )}
      </Container>
    </MarketplaceLayout>
  );
};

export default ArtistProfile;
